/**
 * ImageUpload builder — verbatim from v1 (research/03 §6; REQ-ELEM-2) for any
 * element that predates the drop zone. DIV-012 adds two opt-in extras, each keyed
 * on a field an older element simply does not have, so the v1 output is still
 * what an untouched element renders, byte for byte:
 *
 *   allowedFileTypes  non-empty  → an `accept` attribute and a type check
 *   uploadStyle       'dropzone' → the drop zone markup around the input
 *
 * The zone's own words ship here rather than as merchant fields. "Drag and drop"
 * and "or browse" are a solved convention that no store improves on, and as
 * app-owned phrases they are translated once for every store — a merchant field
 * would have to be translated by each merchant, one string for all their markets.
 *
 * Template-literal markup: the pretty-printed whitespace IS the v1 output.
 */
import { appState } from "../state/app-state";
import { getConditionalDisplay, getToolTipHtml, getElementWidthClass, t } from "./base";
import { allowedExtensions, extensionLabels } from "./file-types";
import { readableAccent, withAlpha } from "./colour";

/** Registered in the API's AppPhrases so both reach the merchant's translation table. */
export const DROP_ZONE_TEXT = "Drag and drop your file here";
export const DROP_ZONE_BROWSE = "or browse";
export const REPLACE_LABEL = "Replace";
export const REMOVE_LABEL = "Remove";

export function buildImageElement(element: any): string {
  var isrequired = element.isRequired;
  var conditionalDisplay = getConditionalDisplay(element);
  let toolTipHtml = getToolTipHtml(element, "span");

  const extensions = allowedExtensions(element.allowedFileTypes);
  const acceptAttr = extensions.length ? `\n        accept='${extensions.join(",")}'` : "";
  const isDropZone = element.uploadStyle === "dropzone";

  const fileInput = `<input
        class='calc-prop cpc-file-upload'
        data-identifier='${element.Identifier}'
        data-isrequired='${isrequired}'
        type='file'
        name="cpc_upload_fileupload_${element.Name}"
        data-property-name="${element.Name}"${acceptAttr}
      />`;

  // Anything that is not the drop zone renders the bare native input, exactly as
  // every file upload element has since v1 — there is no second button variant to
  // diverge from it.
  const control = isDropZone ? dropZone(element, fileInput, extensions) : fileInput;

  return `
    <div class='element${getElementWidthClass(element)}' data-element='${element.Identifier}' ${conditionalDisplay.conditionalTemplate}>
      <label style='color:${appState.calculatorStyling.upperTextColor}'>
        ${t(element.Name)}
        ${toolTipHtml}
      </label>
      ${control}
      <input
        type='hidden'
        class='cpc-file-url-input'
        name="properties[${element.Name}]"
        data-property-name="${element.Name}"
      />
      <span class='cpc-upload-status' style='display:none; margin-left: 10px;'></span>${filledRow(element, isDropZone)}
    </div>
  `.trim();
}

/**
 * The row the drop zone turns into once a file is chosen — rendered here, hidden,
 * rather than assembled in JS, so its theme colours are resolved the same way as
 * the rest of the element and the goldens pin real markup instead of a template.
 *
 * The button style gets none of this: it renders the real `<input type=file>` and
 * the browser draws the filename beside it. The drop zone hides that input behind
 * a label, which hides the browser's filename too, so the state has to be built.
 * Keeping it drop-zone-only also leaves the button style byte-identical to v1.
 *
 * The status lives INSIDE this row — a mark beside the name, the state in the
 * sub-line — rather than on the separate `.cpc-upload-status` line the button
 * style uses. A tick that sits with its file is state, not clutter, so nothing
 * here fades.
 *
 * Replace is a `<label for>` on the same input the zone points at, so replacing
 * needs no script at all. Remove is a real button and MUST stay `type='button'`:
 * the calculator renders inside the theme's product form, where a bare button
 * submits it.
 */
function filledRow(element: any, isDropZone: boolean): string {
  if (!isDropZone) return "";
  const styling = appState.calculatorStyling;
  const text = styling.upperTextColor;
  const accent = readableAccent(styling.priceBackground, styling.upperBackground, text);
  const radius = styling.border === "rounded" ? "8px" : "0";

  return `
      <div class='cpc-drop-zone__file' data-property-name="${element.Name}" style='display:none; align-items:center; gap:12px; padding:13px 14px; text-align:left; border:1.5px solid ${withAlpha(text, 0.22)}; border-radius:${radius}; background:${withAlpha(text, 0.04)}; color:${text};'>
        <span class='cpc-drop-zone__thumb' style='width:44px; height:44px; border-radius:4px; flex:none; display:flex; align-items:center; justify-content:center; overflow:hidden; background:${withAlpha(text, 0.08)}; color:${withAlpha(text, 0.7)}; font-size:10px;'></span>
        <span style='display:flex; flex-direction:column; gap:2px; min-width:0; flex:1;'>
          <span style='display:flex; align-items:center; gap:6px; min-width:0;'>
            <span class='cpc-drop-zone__name' style='font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;'></span>
            <span class='cpc-drop-zone__mark' style='flex:none; display:inline-flex;'></span>
          </span>
          <span class='cpc-drop-zone__state' style='font-size:12px; color:${withAlpha(text, 0.6)};'></span>
        </span>
        <label class='cpc-drop-zone__replace' for='${inputId(element)}' style='flex:none; font-size:12.5px; text-decoration:underline; cursor:pointer; color:${accent};'>${t(REPLACE_LABEL)}</label>
        <button type='button' class='cpc-drop-zone__remove' style='flex:none; font-size:12.5px; text-decoration:underline; cursor:pointer; background:none; border:0; padding:0; color:${withAlpha(text, 0.9)};'>${t(REMOVE_LABEL)}</button>
      </div>`;
}

/** The id that ties the drop zone label to the real input it opens. */
function inputId(element: any): string {
  return `cpc-upload-${element.Identifier}`;
}

/**
 * Every colour here is derived from the theme tokens the calculator already
 * carries, so the zone matches the store without the merchant choosing anything.
 * The fill is a tint of the TEXT colour, never upperBackground — that is the
 * surface the zone sits on, and filling with it makes the zone disappear.
 *
 * The zone is a `<label for>` over the real input rather than a JS click proxy:
 * the browser opens the picker itself, so it keeps working with the keyboard and
 * with assistive tech, and the input stays the same node the delegated change
 * handler and ValidateInputs already find.
 */
function dropZone(element: any, fileInput: string, extensions: string[]): string {
  const styling = appState.calculatorStyling;
  const text = styling.upperTextColor;
  const radius = styling.border === "rounded" ? "8px" : "0";
  // Guarded: a store whose accent IS its background would otherwise draw this link
  // in the surface colour and make it disappear (151 production stores do exactly that).
  const browse = readableAccent(styling.priceBackground, styling.upperBackground, text);
  const typesLine = extensionLabels(extensions);
  const typesHtml = typesLine
    ? `\n        <span class='cpc-drop-zone__types' style='color:${withAlpha(text, 0.55)}; font-size:12px;'>${typesLine}</span>`
    : "";

  return `<label class='cpc-drop-zone' for='${inputId(element)}' style='display:block; text-align:center; padding:28px 20px; cursor:pointer; border:1.5px dashed ${withAlpha(text, 0.28)}; border-radius:${radius}; background:${withAlpha(text, 0.04)}; color:${text};'>
        <span class='cpc-drop-zone__text' style='display:block; font-weight:600;'>${t(DROP_ZONE_TEXT)}</span>
        <span class='cpc-drop-zone__browse' style='display:block; text-decoration:underline; color:${browse};'>${t(DROP_ZONE_BROWSE)}</span>${typesHtml}
      </label>
      ${withId(fileInput, element)}`;
}

/** The input keeps every v1 attribute; the id and the hiding are additions. */
function withId(fileInput: string, element: any): string {
  return fileInput.replace(
    "type='file'",
    `type='file'\n        id='${inputId(element)}'\n        style='position:absolute; width:1px; height:1px; opacity:0; pointer-events:none;'`,
  );
}
